import React, { memo } from "react";
import { classNames } from "helpers";
import { ReactSVG } from "react-svg";
import { InvoiceOutgoingStatusHistory } from "services/api/fleet/vehicles/invoices/types";
import { InvoiceStatusId } from "services/api/fleet/vehicles/invoices/consts";

interface InvoiceStatusToolbarProps {
  statusHistory: InvoiceOutgoingStatusHistory[];
  onToggle: (statusId: string, checked: boolean) => void;
  disabled?: boolean;
  // Status fiskalizacije iz form vrijednosti (ne iz povijesti statusa)
  fiscalizationStatus?: string | null;
  // Trenutni status računa — koristi se za izvedeno stanje kad je povijest nepotpuna
  invoiceStatusId?: string | null;
}

const STATUS_LABELS: Record<string, string> = {
  [InvoiceStatusId.Draft]: "Predložak",
  [InvoiceStatusId.Created]: "Kreiran",
  [InvoiceStatusId.Fiscalized]: "Fiskaliziran",
  [InvoiceStatusId.Sent]: "Poslan",
  [InvoiceStatusId.Paid]: "Plaćen",
};

// Redoslijed napredovanja statusa
const STATUS_ORDER = [
  InvoiceStatusId.Draft,
  InvoiceStatusId.Created,
  InvoiceStatusId.Fiscalized,
  InvoiceStatusId.Sent,
  InvoiceStatusId.Paid,
];

// Statusi koji se ne mogu ručno mijenjati klikom u toolbaru
const LOCKED_STATUSES: string[] = [InvoiceStatusId.Draft, InvoiceStatusId.Created, InvoiceStatusId.Fiscalized];

const InvoiceStatusToolbar: React.FC<InvoiceStatusToolbarProps> = ({
  statusHistory,
  onToggle,
  disabled = false,
  fiscalizationStatus,
  invoiceStatusId,
}) => {
  // Odredi koji statusi trebaju biti "označeni" na temelju invoiceStatusId
  const currentStatusIndex = invoiceStatusId ? STATUS_ORDER.indexOf(invoiceStatusId as any) : -1;

  // Spoji povijest statusa s izvedenim stanjem iz invoiceStatusId
  const orderedStatuses: InvoiceOutgoingStatusHistory[] = STATUS_ORDER.map((id, idx) => {
    const existing = statusHistory.find((h) => h.invoiceStatusId === id);
    const impliedChecked = currentStatusIndex >= 0 && idx <= currentStatusIndex;
    if (existing) {
      return { ...existing, checked: existing.checked || impliedChecked };
    }
    return {
      id: id,
      invoiceStatusId: id,
      statusName: STATUS_LABELS[id] ?? id,
      statusColor: null,
      checked: impliedChecked,
      checkedAt: null,
    };
  });

  const lastCheckedIndex = orderedStatuses.reduce(
    (acc, s, i) => (s.checked ? i : acc),
    -1
  );

  // Fiskalizacija se prati odvojeno od povijesti statusa
  const isFiscalized = fiscalizationStatus === "confirmed";
  const createdIndex = orderedStatuses.findIndex((s) => s.invoiceStatusId === InvoiceStatusId.Created);
  const isCreatedCheckedFromHistory = createdIndex >= 0 && orderedStatuses[createdIndex].checked;
  const isCreatedByStatus = invoiceStatusId !== undefined && invoiceStatusId !== null && invoiceStatusId !== InvoiceStatusId.Draft;
  const isCreatedChecked = isCreatedCheckedFromHistory || isCreatedByStatus;
  // Kreiran ali čeka fiskalizaciju — pokreće amber vizualni tretman
  const isCreatedButNotFiscalized = isCreatedChecked && !isFiscalized;

  return (
    <div className="flex border border-slate-200 rounded-sm overflow-hidden mb-4">
      {orderedStatuses.map((status, index) => {
        const isChecked = status.checked;
        const isCurrent = index === lastCheckedIndex + 1;
        const isDone = isChecked;
        const isLastDone = index === lastCheckedIndex;
        const isPast = isDone && !isLastDone;
        const isLocked = LOCKED_STATUSES.includes(status.invoiceStatusId);
        // Poslan/Plaćen zahtijevaju prethodnu fiskalizaciju
        const requiresFiscalization = (status.invoiceStatusId === InvoiceStatusId.Sent || status.invoiceStatusId === InvoiceStatusId.Paid) && !isFiscalized;
        const isDisabled = disabled || isLocked || (!isCreatedChecked && !isLocked) || requiresFiscalization;

        // "Kreiran" prikazuje amber boju dok čeka fiskalizaciju
        const isCreatedAwaitingFiscal = status.invoiceStatusId === InvoiceStatusId.Created && isCreatedButNotFiscalized && isLastDone;
        // "Fiskaliziran" pulsira kad je sljedeći obavezan korak
        const isFiscalNext = status.invoiceStatusId === InvoiceStatusId.Fiscalized && isCreatedButNotFiscalized && isCurrent;

        return (
          <button
            key={status.invoiceStatusId}
            type="button"
            disabled={isDisabled}
            onClick={() => onToggle(status.invoiceStatusId, !status.checked)}
            className={classNames(
              "flex-1 flex items-center justify-center gap-2 py-2.5 px-3 text-sm transition-all duration-200 border-r last:border-r-0",
              isCreatedAwaitingFiscal
                ? "bg-amber-500 text-white border-amber-500 font-medium"
                : isFiscalNext
                ? "text-amber-700 border-amber-300 font-bold animate-pulse"
                : isDone
                ? "bg-green-100 text-green-800 border-b-2 border-b-green-600 font-semibold"
                : isCurrent
                ? "bg-sky-50 text-sky-700 border-slate-200 font-medium"
                : "bg-white text-slate-400 border-slate-200 font-medium",
              !isDisabled && "cursor-pointer hover:opacity-80"
            )}
            style={isFiscalNext ? {
              background: "repeating-linear-gradient(-45deg, #fef3c7, #fef3c7 5px, #fffbeb 5px, #fffbeb 10px)",
            } : undefined}
          >
            {isDone && (
              <ReactSVG
                src="/media/icons/check.svg"
                className={classNames("w-4 h-4", isCreatedAwaitingFiscal ? "fill-white" : "stroke-green-700")}
              />
            )}
            <span>{status.statusName}</span>
            {/* Uskličnik za privlačenje pažnje na obavezan korak fiskalizacije */}
            {isFiscalNext && <span className="text-xs font-bold">!</span>}
          </button>
        );
      })}
    </div>
  );
};

export default memo(InvoiceStatusToolbar);
